import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  EyeIcon, 
  TrashIcon, 
  ClipboardDocumentIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { FaQrcode } from 'react-icons/fa';
import { workshopAPI } from '../../services/api';
import { useToast } from '../Toast';
import { formatDateToIST } from '../../utils/dateUtils';
import ConfirmationModal from '../ConfirmationModal';

const WorkshopDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [workshop, setWorkshop] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registrationsLoading, setRegistrationsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!id) {
      showToast('Invalid workshop ID', 'error');
      navigate('/workshops');
      return;
    }
    
    fetchWorkshop();
    fetchRegistrations();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWorkshop = async () => {
    try {
      setLoading(true);
      const response = await workshopAPI.getById(id);
      setWorkshop(response.data);
    } catch (error) {
      console.error('Error fetching workshop:', error);
      showToast('Failed to load workshop details', 'error');
      navigate('/workshops');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async () => {
    try {
      setRegistrationsLoading(true);
      const response = await workshopAPI.getRegistrations(id);
      setRegistrations(response.data.registrations);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      showToast('Failed to load registrations', 'error');
    } finally {
      setRegistrationsLoading(false);
    }
  };

  const handleVerifyPayment = async (registrationId) => {
    try {
      await workshopAPI.verifyPayment(registrationId);
      showToast('Payment verified successfully', 'success');
      fetchRegistrations();
    } catch (error) {
      console.error('Error verifying payment:', error);
      showToast('Failed to verify payment', 'error');
    }
  };

  const handleDeleteRegistration = async (registrationId) => {
    setDeleteTarget({ id: registrationId });
    setShowDeleteModal(true);
  };

  const confirmDeleteRegistration = async () => {
    if (!deleteTarget) return;

    try {
      await workshopAPI.deleteRegistration(deleteTarget.id);
      showToast('Registration deleted successfully', 'success');
      fetchRegistrations();
      fetchWorkshop(); // Refresh workshop to update registration count
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting registration:', error);
      showToast('Failed to delete registration', 'error');
    }
  };

  const handleViewScreenshot = async (registrationId) => {
    try {
      const response = await workshopAPI.getPaymentScreenshot(registrationId);
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error fetching payment screenshot:', error);
      showToast('Failed to load payment screenshot', 'error');
    }
  };

  const copyPublicLink = () => {
    const link = `${window.location.origin}/workshop/${workshop.public_link_id}`;
    navigator.clipboard.writeText(link);
    showToast('Public link copied to clipboard!', 'success');
  };

  const formatDate = (dateString) => {
    return formatDateToIST(dateString);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const exportToCSV = () => {
    if (registrations.length === 0) {
      showToast('No registrations to export', 'warning');
      return;
    }

    // Create CSV content
    const headers = ['Name', 'Email', 'Phone', 'Course', 'Year', 'Registration Date', 'Payment Status'];
    const csvContent = [
      headers.join(','),
      ...registrations.map(reg => [
        `"${reg.name}"`,
        `"${reg.email}"`,
        `"${reg.phone}"`,
        `"${reg.course}"`,
        `"${reg.year}"`,
        `"${formatDate(reg.registration_date)}"`,
        `"${reg.payment_verified ? 'Verified' : 'Pending'}"`
      ].join(','))
    ].join('\n');

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${workshop.name}_registrations.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showToast('Registrations exported to CSV successfully!', 'success');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!workshop) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-300">Workshop not found</h3>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/workshops')}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">{workshop.name}</h1>
            <p className="text-gray-400 mt-1">Workshop Details & Registrations</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(`/workshops/${id}/qr-code`)}
            className="inline-flex items-center px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            <FaQrcode className="h-4 w-4 mr-2" />
            QR Code
          </button>
          <button
            onClick={() => navigate(`/workshops/${id}/attendance-qr`)}
            className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <FaQrcode className="h-4 w-4 mr-2" />
            Attendance QR
          </button>
          <button
            onClick={() => navigate(`/workshops/${id}/attendance`)}
            className="inline-flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <CheckCircleIcon className="h-4 w-4 mr-2" />
            View Attendance
          </button>
        </div>
      </div>

      {/* Workshop Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Workshop Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <div>
                  <p className="text-sm text-gray-400">Duration</p>
                  <p className="text-white font-medium">
                    {formatDate(workshop.start_date)} - {formatDate(workshop.end_date)}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div>
                  <p className="text-sm text-gray-400">Fees</p>
                  <p className="text-white font-medium">{formatCurrency(workshop.fees)}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div>
                  <p className="text-sm text-gray-400">Registrations</p>
                  <p className="text-white font-medium">
                    {workshop.total_registrations}
                    {workshop.max_participants && (
                      <span className="text-gray-500"> / {workshop.max_participants}</span>
                    )}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div>
                  <p className="text-sm text-gray-400">Status</p>
                  <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${
                    workshop.status === 'active' 
                      ? 'bg-green-500 text-white' 
                      : workshop.status === 'completed'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-500 text-white'
                  }`}>
                    {workshop.status.charAt(0).toUpperCase() + workshop.status.slice(1)}
                  </span>
                </div>
              </div>
            </div>
            
            {workshop.description && (
              <div className="mt-6 pt-6 border-t border-gray-700">
                <h3 className="text-sm font-medium text-gray-400 mb-2">Description</h3>
                <p className="text-white">{workshop.description}</p>
              </div>
            )}
            
            {workshop.whatsapp_group_link && (
              <div className="mt-6 pt-6 border-t border-gray-700">
                <h3 className="text-sm font-medium text-gray-400 mb-2">WhatsApp Group</h3>
                <a 
                  href={workshop.whatsapp_group_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  Join WhatsApp Group
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Public Link */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Public Registration Link</h2>
          <div className="space-y-4">
            <div className="bg-gray-700 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-1">Share this link with students:</p>
              <p className="text-sm text-blue-400 break-all">
                {window.location.origin}/workshop/{workshop.public_link_id}
              </p>
            </div>
            <button
              onClick={copyPublicLink}
              className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
              Copy Link
            </button>
          </div>
        </div>
      </div>

      {/* Registrations */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Registrations ({registrations.length})</h2>
          {registrations.length > 0 && (
            <button
              onClick={exportToCSV}
              className="inline-flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              title="Export registrations to CSV"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          )}
        </div>
        
        {registrationsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : registrations.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-gray-300">No registrations yet</h3>
            <p className="mt-1 text-sm text-gray-500">Share the public link to get registrations</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-700">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-1/6">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-1/5">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-1/4">
                    Course & Year
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-1/6">
                    Registration Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider w-1/6">
                    Payment Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider w-1/6">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {registrations.map((registration) => (
                  <tr key={registration._id} className="hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-white">{registration.name}</div>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <div className="text-sm text-gray-300 break-all">{registration.email}</div>
                      <div className="text-sm text-gray-400">{registration.phone}</div>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <div className="text-sm text-gray-300 break-words leading-relaxed">{registration.course}</div>
                      <div className="text-sm text-gray-400">{registration.year}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {formatDate(registration.registration_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                        registration.payment_verified 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {registration.payment_verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {registration.payment_screenshot_filename && (
                          <button
                            onClick={() => handleViewScreenshot(registration._id)}
                            className="text-blue-400 hover:text-blue-300 transition-colors"
                            title="View payment screenshot"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        {!registration.payment_verified && (
                          <button
                            onClick={() => handleVerifyPayment(registration._id)}
                            className="text-green-400 hover:text-green-300 transition-colors"
                            title="Verify payment"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteRegistration(registration._id)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                          title="Delete registration"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Registration Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteRegistration}
        title="Confirm Deletion"
        message="Are you sure you want to delete this registration? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        confirmButtonVariant="danger"
      />
    </div>
  );
};

export default WorkshopDetail; 